import { createEnhancedInteractionData } from '../../utils/interactionUtils';
import { getMedicationDisplayName } from '../../utils/medicationUtils';
import { asDisplayMedications } from '../../utils/guestMedications';
import { riskChipLevel } from '../stack/RiskChip';
import { flaggedWith, notFlaggedWith } from './copy';

// Turning a check's answer into v5.1's result rows (STACK-587; design doc §3
// step 6, §3.2). Pure, so the rules that keep a result honest are asserted
// without rendering:
//
//   - Rows come only from a `200 completed` answer. The route lists only the
//     pairs it found an interaction for, so a picked pair absent from a
//     completed answer was checked and found none: "Not flagged with …".
//     Anything else is couldn't-check, never a clean row (STACK-528's rule).
//   - A pair listed with a risk we cannot read (not High, Medium or Low) is
//     not a clean pair either: the whole answer is unreadable, and the page
//     shows couldn't-check rather than a row that reads clean.
//   - One row per picked supplement, flagged first, worst first; ties keep
//     the order the visitor picked them in.

const RISK_ORDER = { high: 3, medium: 2, low: 1 };

/** True for the one answer rows may be built from. */
export const isCompletedAnswer = (response) =>
  Boolean(response) && response.status === 'completed' && !response.isProcessing
  && Array.isArray(response.interactions);

/**
 * @param {Object} response - the route's completed answer
 * @param {Array} medications - the picks `{ in_rxcui, ingredient_name, display_name }`
 * @param {Array} supplements - the picks `{ key, name }`
 * @returns {Array|null} rows `{ key, name, level, sub, flaggedRxcuis, interaction }`,
 *   or null when the answer is not a completed one, or lists a picked pair
 *   with a risk it cannot read
 */
export const buildResultRows = (response, medications, supplements) => {
  if (!isCompletedAnswer(response)) return null;
  const pickedRxcuis = new Set(medications.map((m) => String(m.in_rxcui)));
  const pickedKeys = new Set(supplements.map((s) => s.key));
  const unreadable = response.interactions.some((i) => i && pickedKeys.has(i.supplement_key)
    && pickedRxcuis.has(String(i.medication_in_rxcui))
    && typeof i.risk === 'string' && !RISK_ORDER[i.risk.toLowerCase()]);
  if (unreadable) return null;
  const displayMeds = asDisplayMedications(medications);
  const nameOf = (rxcui) => getMedicationDisplayName(rxcui, displayMeds);
  const allNames = medications.map((m) => m.display_name);

  const rows = supplements.map((s, order) => {
    // Only the pairs this check asked about count.
    const found = response.interactions.filter((i) => i && i.supplement_key === s.key
      && pickedRxcuis.has(String(i.medication_in_rxcui)) && typeof i.risk === 'string');
    const interaction = createEnhancedInteractionData(found, nameOf);
    const level = riskChipLevel({ interaction });
    const flaggedRxcuis = new Set(found.map((i) => String(i.medication_in_rxcui)));
    const flaggedNames = medications
      .filter((m) => flaggedRxcuis.has(String(m.in_rxcui)))
      .map((m) => m.display_name);
    return {
      key: s.key,
      name: s.name,
      order,
      level,
      sub: level ? flaggedWith(flaggedNames) : notFlaggedWith(allNames),
      flaggedRxcuis: [...flaggedRxcuis],
      interaction,
    };
  });

  return rows
    .sort((a, b) => (RISK_ORDER[b.level] || 0) - (RISK_ORDER[a.level] || 0) || a.order - b.order)
    .map(({ order, ...row }) => row);
};

/**
 * Every picked pair with the risk found, `'none'` for a pair checked clean —
 * the `pairs` of the result event (design doc §5). Never reasoning text.
 */
export const pairsForAnalytics = (rows, medications) => rows.flatMap((row) => medications.map((m) => {
  const hit = row.interaction.allInteractions.find((i) => String(i.medication_in_rxcui) === String(m.in_rxcui));
  return {
    supplement_key: row.key,
    in_rxcui: m.in_rxcui,
    risk: hit ? String(hit.risk).toLowerCase() : 'none',
  };
}));
